'use client';

import { useEffect } from 'react';

import { usePathname } from 'next/navigation';

import Lenis from '~/lib/vendor/lenis/lenis';
import '~/lib/vendor/lenis/lenis.css';

/** Matches RTEO's desktop easing; touch, dialogs and reduced motion stay native. */
export function WebsiteSmoothScroll() {
  const pathname = usePathname();
  useEffect(() => {
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = matchMedia('(hover: hover) and (pointer: fine)');
    let scroller: Lenis | null = null;
    const destroy = () => {
      scroller?.destroy();
      scroller = null;
    };
    const update = () => {
      destroy();
      if (document.hidden || reduced.matches || !desktop.matches) return;
      scroller = new Lenis({
        autoRaf: true,
        lerp: 0.2,
        smoothWheel: true,
        syncTouch: false,
        wheelMultiplier: 1,
        anchors: false,
        allowNestedScroll: true,
        prevent: (node) =>
          Boolean(
            node.closest(
              '[data-lenis-prevent], [role="dialog"], textarea, select',
            ),
          ),
      });
    };
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = (
        event.target as Element | null
      )?.closest<HTMLAnchorElement>('a[href]');
      if (
        !anchor ||
        anchor.download ||
        (anchor.target && anchor.target !== '_self')
      )
        return;
      const url = new URL(anchor.href, location.href);
      if (
        url.origin !== location.origin ||
        url.pathname !== location.pathname ||
        !url.hash ||
        url.hash === '#site-content'
      )
        return;
      let id: string;
      try {
        id = decodeURIComponent(url.hash.slice(1));
      } catch {
        return;
      }
      const target = document.getElementById(id);
      if (!target) return;
      const header = document
        .querySelector('[data-site-header-logo]')
        ?.closest('header');
      const offset = (header?.getBoundingClientRect().height ?? 80) + 16;
      const top = Math.max(
        0,
        target.getBoundingClientRect().top + scrollY - offset,
      );
      event.preventDefault();
      if (scroller) scroller.scrollTo(top);
      else
        window.scrollTo({
          top,
          behavior: reduced.matches ? 'instant' : 'smooth',
        });
      history.pushState(null, '', url.hash);
    };
    update();
    document.addEventListener('click', onClick, true);
    document.addEventListener('visibilitychange', update);
    reduced.addEventListener('change', update);
    desktop.addEventListener('change', update);
    return () => {
      destroy();
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('visibilitychange', update);
      reduced.removeEventListener('change', update);
      desktop.removeEventListener('change', update);
    };
  }, [pathname]);
  return null;
}
